import { useTranslation } from 'react-i18next'

import {
  type PlanTableColumn,
  type PlanTableColumnSpec,
  type PlanTableHighlightedCells,
  type PlanTableInternalInconsistency,
  type PlanTableMismatchFocus,
  PlanTable as SharedPlanTable,
  formatSignedValue,
  formatValue,
} from '~/components/planSubmit/PlanTable'
import type { SlotValue } from '~/pages/ProcurementPlanDetail/models/calc'
import {
  type BreakdownColumn,
  type ColumnSide,
  aggregateColumnsByCounterparty,
} from '~/pages/ProcurementPlanDetail/models/columns'
import type { PlanDetailDeeplink } from '~/pages/ProcurementPlanDetail/models/deeplink'
import type { DisplayMode } from '~/pages/ProcurementPlanDetail/models/displayMode'
import type { InternalInconsistencyView } from '~/pages/ProcurementPlanDetail/models/internalInconsistency'
import {
  type MismatchTableState,
  matchingColumnIndexes,
  resolveMismatchFocusCell,
  resolveMismatchHighlight,
} from '~/pages/ProcurementPlanDetail/models/mismatchItems'

type Props = {
  /** 時刻ラベル ("00:00" 〜 "23:30") */
  timeLabels: readonly string[]
  /** 需要計画値 48 スロット */
  demandSlots: SlotValue[]
  /** 需要計画値の前回提出 48 スロット（hover tooltip 用）。前回提出が無い場合は全コマ undefined。 */
  demandPreviousSlots: SlotValue[]
  /** 調達取引先別内訳列 */
  procurementColumns: BreakdownColumn[]
  /** 調達合計（frontend 算出、各コマ） */
  procurementTotals: SlotValue[]
  /** 販売取引先別内訳列 */
  salesColumns: BreakdownColumn[]
  /** 販売合計（frontend 算出、各コマ） */
  salesTotals: SlotValue[]
  /** 需給バランス（frontend 算出、各コマ）= 調達計 − 販売計 */
  balances: SlotValue[]
  /** 需要計画値の前回差分フラグ */
  demandDiffFlags: boolean[]
  /** 需給バランスの前回差分フラグ (AC-PPD-020) */
  balanceDiffFlags: boolean[]
  /** 前回提出データが存在するか */
  hasPreviousSubmitted: boolean
  /**
   * 表示モード（取引先毎合計 / 取引毎）。選択 UI は DetailFilterBar に移設済み
   * (procurement-plan-detail-filter 設計書 §5, design `#sd-view-seg`)。
   * 本コンポーネントは props をそのまま列集計の切替にのみ使う。
   */
  displayMode: DisplayMode
  /** 提出管理からの deeplink で一時強調する取引先・範囲 (AC-PPD-022)。無ければ強調しない */
  deeplinkHighlight?: PlanDetailDeeplink
  /**
   * 未解消の取引相手間ミスマッチの箇所と不整合バナーで注目中の件 (AC-PPD-009〜012 / 016)。
   * `useMismatchNavigation` の `table` をそのまま渡す。無い・件が空なら強調しない
   */
  mismatch?: MismatchTableState
  /** 計画内不整合のコマの強調 (AC-PPD-024 / 025)。無ければ強調しない (出す条件は呼び出し元が決める) */
  internalInconsistency?: InternalInconsistencyView
}

/** 合計列の spec key (AC-PPD-012 の強調先) */
const TOTAL_KEY_BY_SIDE: Record<ColumnSide, string> = {
  procurement: 'procurementTotal',
  sales: 'salesTotal',
}

/**
 * 共通 PlanTable に渡す列 key。JEPX 時間前は調達・販売の両方で同じ列 key を持つので、側を
 * 前置して表全体で一意にする (片側の強調がもう片側の同じ列に漏れないため)。
 */
function cellKey(side: ColumnSide, columnKey: string): string {
  return `${side}/${columnKey}`
}

/**
 * deeplink の取引先コード (AC-PPD-022-1) と側から、表示中の内訳列のうち強調する列を引く。
 * 照合は不整合の常時強調と同じ `matchingColumnIndexes` (「取引毎」表示では同じ取引先の契約列
 * がすべて当たり、JEPX 市場列は固定コードで当たる)。一致する列が
 * 無ければ undefined (通常表示。送り側は自社内の不整合やマスタ未登録の他社も渡しうる)。
 */
function resolveHighlightedCells(
  deeplink: PlanDetailDeeplink | undefined,
  columnsBySide: Record<ColumnSide, BreakdownColumn[]>,
): PlanTableHighlightedCells | undefined {
  if (deeplink === undefined) return undefined
  const columns = columnsBySide[deeplink.side]
  const columnKeys = matchingColumnIndexes(
    columns,
    deeplink.partnerCode,
  ).flatMap((index) => {
    const column = columns[index]
    return column === undefined ? [] : [cellKey(deeplink.side, column.key)]
  })
  if (columnKeys.length === 0) return undefined
  return {
    columnKeys,
    fromIndex: deeplink.fromIndex,
    toIndexExclusive: deeplink.toIndexExclusive,
  }
}

function toPlanTableColumn(
  side: ColumnSide,
  col: BreakdownColumn,
): PlanTableColumn {
  return {
    key: cellKey(side, col.key),
    // 取引先列は counterpartyName、無ければ sourceLabel（sourceType の日本語ラベル）へ
    // フォールバックする（市場列は fixedLabel 経由で別途描画されるため label は空でよい）。
    label: col.counterpartyName ?? col.sourceLabel,
    counterpartyCode: col.counterpartyCode,
    slots: col.slots,
    diffFlags: col.diffFlags,
    fixedLabel: col.fixedLabel,
    previousSlots: col.previousSlots,
  }
}

/**
 * 需要調達計画 48 コマテーブル（read-only）。
 *
 * 計画作成 API（CreateProcurementPlan）が計画値を生成するため、本テーブルは
 * 表示専用（= EditableCell なし）。編集 callback prop も不要。
 *
 * 列構成（spec AC-PPD-002、2 行 thead）:
 *   1 行目: 時刻(rowspan2) | 需給バランス(rowspan2) | 需要計画値(rowspan2)
 *           | 調達計(rowspan2) | 調達 (N)(colspan=N)
 *           | 販売計(rowspan2) | 販売 (M)(colspan=M)
 *   2 行目: 調達×N の counterparty ヘッダ … | 販売×M の counterparty ヘッダ …
 *
 * セル描画・色定数・罫線モデルは共通 `components/planSubmit/PlanTable`
 * （GenerationSalesPlanDetail と 1 本化）に委譲し、本ファイルは列の表示順
 * （調達計 → 調達群 → 販売計 → 販売群。各合計をその内訳の直前に置く）と
 * i18n ラベル解決のみを担う。旧「差」列（需給バランスと同値の末尾列）は
 * AC-PPD-002 の列挙に無い既存拡張だったため廃止した（procurement-plan-detail
 * -column-order-sort-zero-retention 設計書 D3）。
 */
export const PlanTable = ({
  timeLabels,
  demandSlots,
  demandPreviousSlots,
  procurementColumns,
  procurementTotals,
  salesColumns,
  salesTotals,
  balances,
  demandDiffFlags,
  balanceDiffFlags,
  hasPreviousSubmitted,
  displayMode,
  deeplinkHighlight,
  mismatch,
  internalInconsistency,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // 前回差分 hover tooltip の文言を組み立てる（plan-diff-cell-tooltip 設計書 Decision 3:
  // previous/current のいずれかが未定義（前回提出なし・前回コマ未入力）なら undefined を
  // 返し tooltip を出さない。「前回値: undefined (差分 NaN)」を避ける）。
  const previousDiffTooltip = (
    previous: SlotValue,
    current: SlotValue,
  ): string | undefined => {
    if (previous === undefined || current === undefined) return undefined
    return t('page:procurementPlanDetail.previousDiff.tooltip', {
      previous: formatValue(previous, ''),
      diff: formatSignedValue(current - previous, ''),
    })
  }

  // AC-12: 表示モード（取引先毎合計 = 既定 / 取引毎）。選択 UI (DetailFilterBar) と
  // state は index.tsx に集約済み。調達合計・販売合計・需給バランス
  // (procurementTotals/salesTotals/balances props) は呼び出し元が表示モード切替前の
  // 内訳列から算出したものをそのまま渡し続けるため、displayMode はテーブルの列表示
  // にのみ影響する (設計書 §4 主要意思決定「表示ビューの切替のみ」)。
  const displayProcurementColumns =
    displayMode === 'perCounterpartyTotal'
      ? aggregateColumnsByCounterparty(procurementColumns)
      : procurementColumns
  const displaySalesColumns =
    displayMode === 'perCounterpartyTotal'
      ? aggregateColumnsByCounterparty(salesColumns)
      : salesColumns

  const displayColumnsBySide: Record<ColumnSide, BreakdownColumn[]> = {
    procurement: displayProcurementColumns,
    sales: displaySalesColumns,
  }
  const highlightedCells = resolveHighlightedCells(
    deeplinkHighlight,
    displayColumnsBySide,
  )
  // AC-PPD-009〜012: 当たる列は表示モード適用後の列で引く (取引毎表示では契約列がすべて当たる)
  const mismatchHighlight =
    mismatch === undefined || mismatch.items.length === 0
      ? undefined
      : resolveMismatchHighlight({
          items: mismatch.items,
          columnsBySide: displayColumnsBySide,
          totalKeyBySide: TOTAL_KEY_BY_SIDE,
          cellKey,
        })
  const mismatchFocus = mismatch?.focus
  const focusCell =
    mismatchFocus === undefined
      ? undefined
      : resolveMismatchFocusCell({
          item: mismatchFocus.item,
          columnsBySide: displayColumnsBySide,
          cellKey,
        })
  const sharedMismatchFocus: PlanTableMismatchFocus | undefined =
    focusCell === undefined || mismatchFocus === undefined
      ? undefined
      : { ...focusCell, navigationId: mismatchFocus.navigationId }

  // AC-PPD-024 / 025: 需給バランスのセルと需要計画値のセルの warning 区分の強調と tooltip
  const sharedInternalInconsistency:
    | PlanTableInternalInconsistency
    | undefined =
    internalInconsistency === undefined
      ? undefined
      : {
          balanceRows: new Map(
            [...internalInconsistency.balanceRows].map((row) => [
              row,
              t(
                'page:procurementPlanDetail.internalInconsistency.balanceLabel',
              ),
            ]),
          ),
          keyRows: new Map(
            [...internalInconsistency.retailerGaps].map(([row, diff]) => [
              row,
              t(
                'page:procurementPlanDetail.internalInconsistency.retailerTooltip',
                { diff: formatSignedValue(diff, '') },
              ),
            ]),
          ),
        }

  // AC-PPD-002: 各合計列をその内訳群の直前に置く（調達計 → 調達群 → 販売計 → 販売群）。
  const columnSpecs: PlanTableColumnSpec[] = [
    {
      kind: 'total',
      key: TOTAL_KEY_BY_SIDE.procurement,
      variant: 'procurement',
      label: t('page:procurementPlanDetail.table.procurementTotal'),
      values: procurementTotals,
    },
    {
      kind: 'group',
      key: 'procurement',
      variant: 'procurement',
      groupLabel: t('page:procurementPlanDetail.table.procurementGroup', {
        count: displayProcurementColumns.length,
      }),
      columns: displayProcurementColumns.map((col) =>
        toPlanTableColumn('procurement', col),
      ),
    },
    {
      kind: 'total',
      key: TOTAL_KEY_BY_SIDE.sales,
      variant: 'sales',
      label: t('page:procurementPlanDetail.table.salesTotal'),
      values: salesTotals,
    },
    {
      kind: 'group',
      key: 'sales',
      variant: 'sales',
      groupLabel: t('page:procurementPlanDetail.table.salesGroup', {
        count: displaySalesColumns.length,
      }),
      columns: displaySalesColumns.map((col) =>
        toPlanTableColumn('sales', col),
      ),
    },
  ]

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        gap: '8px',
      }}
    >
      <SharedPlanTable
        balanceDiffFlags={balanceDiffFlags}
        balanceHeaderLabel={t(
          'page:procurementPlanDetail.table.supplyDemandBalance',
        )}
        balances={balances}
        columnSpecs={columnSpecs}
        dailyTotalLabel={t('page:procurementPlanDetail.table.dailyTotal')}
        dailyTotalTooltip={t(
          'page:procurementPlanDetail.table.dailyTotalTooltip',
        )}
        emptyLabel={t('page:procurementPlanDetail.cell.empty')}
        hasPreviousSubmitted={hasPreviousSubmitted}
        highlightedCells={highlightedCells}
        internalInconsistency={sharedInternalInconsistency}
        keyColumn={{
          headerLabel: t('page:procurementPlanDetail.table.demandPlan'),
          values: demandSlots,
          diffFlags: demandDiffFlags,
          previousValues: demandPreviousSlots,
        }}
        mismatchFocus={sharedMismatchFocus}
        mismatchHighlight={mismatchHighlight}
        noCounterpartyCodeLabel={t('page:procurementPlanDetail.header.empty')}
        previousDiffTooltip={previousDiffTooltip}
        timeHeaderLabel={t('page:procurementPlanDetail.table.time')}
        timeLabels={timeLabels}
      />
    </div>
  )
}
